import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp } from "lucide-react";
import { api, qs, type EventPropsResponse, type EventsResponse } from "../lib/api";
import { nf } from "../lib/format";
import { ErrorBox, PageSpinner } from "../components/ui";
import type { SiteOutletContext } from "./SiteLayout";

function formatProps(data: string | null): string {
  if (!data) return "—";
  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    const parts = Object.entries(parsed).map(([key, value]) => {
      const text = typeof value === "object" && value !== null ? JSON.stringify(value) : String(value);
      return `${key}: ${text}`;
    });
    return parts.length > 0 ? parts.join(", ") : "—";
  } catch {
    return data;
  }
}

export default function EventsPage() {
  const { site, range } = useOutletContext<SiteOutletContext>();
  const [selected, setSelected] = useState<string | null>(null);

  const events = useQuery({
    queryKey: ["events", site.id, range],
    queryFn: () => api.get<EventsResponse>(`/api/sites/${site.id}/events${qs({ from: range.from, to: range.to, unit: range.unit })}`),
  });

  const props = useQuery({
    queryKey: ["event-props", site.id, selected, range],
    queryFn: () =>
      api.get<EventPropsResponse>(
        `/api/sites/${site.id}/event-props${qs({ name: selected ?? "", from: range.from, to: range.to, unit: range.unit })}`,
      ),
    enabled: Boolean(selected),
  });

  if (events.isLoading) return <PageSpinner />;
  if (events.error || !events.data) return <ErrorBox message="Gagal memuat data peristiwa." />;

  const rows = events.data.events;

  return (
    <div className="space-y-4">
      <section className="card overflow-hidden">
        {rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">
            Belum ada peristiwa kustom pada rentang ini.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                <th className="px-4 py-2.5">Peristiwa</th>
                <th className="px-4 py-2.5 text-right">Jumlah</th>
                <th className="px-4 py-2.5 text-right">Pengunjung</th>
                <th className="px-4 py-2.5 text-right">Kunjungan</th>
                <th className="w-10 px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.name}
                  className={`cursor-pointer border-b border-slate-50 transition hover:bg-slate-50 ${
                    selected === row.name ? "bg-emerald-50/50" : ""
                  }`}
                  onClick={() => setSelected(selected === row.name ? null : row.name)}
                >
                  <td className="px-4 py-2.5 font-medium text-slate-700">{row.name}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{nf.format(row.count)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{nf.format(row.visitors)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{nf.format(row.visits)}</td>
                  <td className="px-4 py-2.5 text-slate-400">
                    {selected === row.name ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected && (
        <section className="card overflow-hidden">
          <header className="border-b border-slate-100 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-slate-800">
              Properti: <span className="text-emerald-700">{selected}</span>
            </h3>
          </header>
          {props.isLoading ? (
            <PageSpinner />
          ) : props.error || !props.data ? (
            <p className="px-4 py-6 text-sm text-red-600">Gagal memuat properti.</p>
          ) : props.data.props.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-400">Tidak ada properti terekam.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2">Data</th>
                  <th className="px-4 py-2 text-right">Jumlah</th>
                  <th className="px-4 py-2 text-right">Pengunjung</th>
                </tr>
              </thead>
              <tbody>
                {props.data.props.map((row, index) => (
                  <tr key={index} className="border-b border-slate-50">
                    <td className="px-4 py-2 text-slate-700">{formatProps(row.data)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{nf.format(row.count)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{nf.format(row.visitors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
